import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Lede,
  Card,
} from '../AumhumSite/sections'
import {
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
} from '../AumhumSite/icons'
import {
  clearPendingAssessmentResult,
  fetchMyProspectBundle,
  isLoggedIn,
  optOutOfWellnessFunnel,
  PROSPECT_MOCK_MODE,
  readPendingAssessmentResult,
  saveAssessmentResult,
} from 'src/consumer/services/patientProspectService'
import {
  PROSPECT_DRAFT_KEY,
  PROSPECT_DRAFT_TTL_MS,
} from '../PatientProspectSignup/constants'
import CareStatusIntakeForm from './CareStatusIntakeForm'
import '../PatientProspectSignup/PatientProspectSignup.css'
import './CareStatusIntake.css'

// /care/status — the post-login dashboard for ROLE_PATIENT_PROSPECT users.
// Renders three sections in order:
//   1. Status banner (submitted / pathway recap / contact card)
//   2. Recommendation card if the triage engine has produced one
//   3. Progressive intake form (collapsible sections + reports upload),
//      backed by PATCH /api/secure/me/patient-prospect/intake and
//      POST /api/secure/me/patient-prospect/reports.
//
// Data flow: `fetchMyProspectBundle` returns the authoritative server-side
// bundle in real mode (or the mock-mode equivalent constructed from the
// signup mock session). The pre-signup draft in localStorage is used only
// as a fallback for the very brief window between signup completing and
// the first server fetch.

function readDraft() {
  try {
    const raw = window.localStorage.getItem(PROSPECT_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PROSPECT_DRAFT_TTL_MS) {
      window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const FALLBACK_CONTACT = {
  whatsapp: 'https://wa.me/918047492952',
  phone: '+918047492952',
  chatHandle: 'aumhum-care',
}

const PATHWAY_LABEL = {
  PSYCHIATRY: 'Mental health',
  THERAPY: 'Mental health',
  BIOLOGICAL: 'Biological',
  CHILD: 'Child growth & care',
  ASSESSMENT: 'Not sure / something else',
}
const SPECIALTY_TOP_LABEL = {
  endocrinology: 'Hormonal health',
  rheumatology: 'Immune system',
}
const SPECIALTY_SUB_LABEL = {
  psychiatry: 'Psychiatry',
  psychology: 'Therapy / Psychology',
}

// Pick the clinician-validated screener that best matches what the
// prospect already told us (pathway / specialty / presenting concerns).
// Returns null when nothing matches confidently — the UI then falls
// back to a generic "Start a self-assessment" CTA without a specific
// recommendation. Slugs match what AssessmentPlayer's
// /assessment/:slug route consumes.
//
// Priority order:
//   1. presentingConcerns chips the user explicitly picked
//   2. specialtyTag the support team is going to triage against
//   3. pathway picked on /care/start
//
// Each row maps to ONE recommended assessment so the suggestion is
// actionable (a list of three would just be the same as the catalog).
function pickRecommendedAssessment(prospect) {
  if (!prospect) return null
  const concerns = Array.isArray(prospect.intakeAnswers?.presentingConcerns)
    ? prospect.intakeAnswers.presentingConcerns.map((c) => String(c).toLowerCase())
    : []
  const specialty = String(prospect.specialtyTag || '').toLowerCase()
  const pathway = String(prospect.pathway || '').toUpperCase()
  // 1) presenting-concerns chips — most specific.
  const hasAny = (...tokens) => concerns.some((c) => tokens.some((t) => c.includes(t)))
  if (hasAny('depress', 'low mood', 'sad')) {
    return { slug: 'phq-9', name: 'PHQ-9 (Depression screener)', why: 'Quick screen for low-mood signs you mentioned' }
  }
  if (hasAny('anxi', 'worry', 'panic')) {
    return { slug: 'gad-7', name: 'GAD-7 (Anxiety screener)', why: 'Quick screen for the anxiety signs you mentioned' }
  }
  if (hasAny('sleep', 'insomn')) {
    return { slug: 'psqi', name: 'PSQI (Sleep quality)', why: 'Quick screen for the sleep concerns you mentioned' }
  }
  if (hasAny('stress', 'burnout')) {
    return { slug: 'pss-10', name: 'PSS-10 (Perceived stress)', why: 'Quick screen for the stress you described' }
  }
  // 2) specialty tag from the funnel.
  if (specialty === 'psychiatry' || specialty === 'psychology') {
    return { slug: 'phq-9', name: 'PHQ-9 (Depression screener)', why: 'A common starting point for talk-therapy / psychiatry pathways' }
  }
  if (specialty === 'endocrinology') {
    return { slug: 'pss-10', name: 'PSS-10 (Perceived stress)', why: 'A common companion to hormonal-health intake' }
  }
  // 3) pathway from /care/start.
  if (pathway === 'PSYCHIATRY' || pathway === 'THERAPY') {
    return { slug: 'phq-9', name: 'PHQ-9 (Depression screener)', why: 'A common starting point for mental-health pathways' }
  }
  return null
}
function humaniseTopLabel(pathway, specialtyTag) {
  if (specialtyTag && SPECIALTY_TOP_LABEL[specialtyTag]) return SPECIALTY_TOP_LABEL[specialtyTag]
  if (pathway && PATHWAY_LABEL[pathway]) return PATHWAY_LABEL[pathway]
  return 'Healthcare'
}
function humaniseSubLabel(specialtyTag) {
  if (!specialtyTag) return null
  return SPECIALTY_SUB_LABEL[specialtyTag] || null
}

function deriveRecap(bundle, draft) {
  const p = bundle?.prospect
  if (p?.pathway || p?.specialtyTag) {
    return {
      topLabel: humaniseTopLabel(p.pathway, p.specialtyTag),
      subLabel: humaniseSubLabel(p.specialtyTag),
    }
  }
  if (draft?.resolved) return draft.resolved
  return null
}

function deriveFirstName(bundle) {
  const name = bundle?.prospect?.name
  if (!name) return 'there'
  return name.split(' ')[0]
}

export default function CareStatusView() {
  const [bundle, setBundle] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loadingBundle, setLoadingBundle] = useState(false)
  const signedIn = useMemo(isLoggedIn, [])
  const [searchParams] = useSearchParams()
  // MobileOtpStep redirects here with `?initFailed=1&reason=…` when the
  // user successfully verified their OTP (so they ARE signed in) but the
  // /me/patient-prospect/initialize call failed afterwards. Without this
  // notice the user lands on the "No active care request found" empty
  // state with no idea why their care match didn't go through.
  const initFailed = searchParams.get('initFailed') === '1'
  const initFailReason = searchParams.get('reason') || ''
  // /assessment → /signup → /care/status carries this; the AssessmentPlayer's
  // result page also links here directly with the same flag for already-
  // signed-in users. Tells us to (a) replay the stashed take if we have
  // one, (b) anchor the intake form so the user lands on the "share more
  // details" step.
  const fromAssessment = searchParams.get('fromAssessment') === '1'
  const intakeFormRef = useRef(null)
  const [optingOut, setOptingOut] = useState(false)

  useEffect(() => {
    setDraft(readDraft())
    if (PROSPECT_MOCK_MODE || signedIn) {
      // Anonymous-then-signup replay: if the AssessmentPlayer stashed a
      // take in sessionStorage before /signup, we now (post-auth) own
      // the rights to write it to the per-user store. Best-effort —
      // clearing the stash even on failure prevents a stuck retry loop
      // (the take is also still in the legacy /assessment/{id}/ans
      // store from the original save, so this is sidecar).
      const pending = readPendingAssessmentResult()
      const replayPromise = pending
        ? saveAssessmentResult(pending).finally(() => clearPendingAssessmentResult())
        : Promise.resolve(null)

      setLoadingBundle(true)
      // Fetch bundle AFTER replay so the recentAssessments rail
      // includes the just-replayed take on first paint.
      replayPromise
        .then(() => fetchMyProspectBundle())
        .then((b) => setBundle(b))
        .catch(() => setBundle(null))
        .finally(() => setLoadingBundle(false))
    }
  }, [signedIn])

  // ?fromAssessment=1 → scroll to the intake form once the bundle has
  // loaded. We wait for the bundle (rather than scrolling on mount)
  // because the form is conditionally rendered on `hasProspectData`.
  useEffect(() => {
    if (!fromAssessment) return
    if (!bundle?.prospect) return
    if (!intakeFormRef.current) return
    // Tiny defer so the DOM has finished laying out the just-rendered
    // sections above the form.
    const t = setTimeout(() => {
      try {
        intakeFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } catch {
        // older browsers: ignore
      }
    }, 120)
    return () => clearTimeout(t)
  }, [fromAssessment, bundle])

  // Wellness opt-out — wired to the "Just here for the AUMHUM wellness
  // app?" link. Best-effort flip to STATUS_WELLNESS_OPT_OUT before nav
  // so support dashboards filter the abandoned record out of "active
  // prospects". Navigates regardless on failure; we never want to trap
  // the user on /care/status.
  const handleWellnessOptOut = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    if (optingOut) return
    setOptingOut(true)
    try {
      await optOutOfWellnessFunnel()
    } catch {
      // ignore — we navigate anyway
    } finally {
      // Mark the visit to /wellness as an explicit user choice so
      // ConsumerHomepage's auto-portal-selector doesn't bounce them
      // back to /care/status. Without this flag, a multi-role user
      // (still has ROLE_PATIENT_PROSPECT until support fully drops
      // the role band) gets re-routed by the auto-router and ends
      // up right where they started.
      try {
        window.sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        window.sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        window.localStorage.setItem('lastChosenFlow', 'consumer')
      } catch {
        // non-fatal
      }
      // /wellness is the canonical wellness home — taking the user
      // straight there keeps the URL stable and matches the
      // namespaced portal layout. RootLanding would redirect / to
      // /wellness anyway; this just skips the hop.
      window.location.assign('/wellness')
    }
  }

  const recap = deriveRecap(bundle, draft)
  const reco = bundle?.recommendation
  const firstName = deriveFirstName(bundle)
  const submittedAt = bundle?.prospect?.createdAt
  const hasProspectData = !!bundle?.prospect

  // Empty state — neither a server bundle nor a usable draft.
  if (!hasProspectData && !recap) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Care status</Eyebrow>
        <SectionTitle as="h1">
          {initFailed
            ? 'You’re signed in. We just couldn’t finish the matching step.'
            : 'No active care request found'}
        </SectionTitle>
        <Lede>
          {initFailed
            ? 'Your account is set up, but the request to match you with a specialist didn’t go through. Try once more — it only takes a minute.'
            : 'To get matched with a specialist, start a new request — it only takes a minute.'}
        </Lede>
        {initFailed && initFailReason ? (
          <div
            className="pps-recap"
            role="status"
            style={{ marginTop: '0.75rem', borderColor: 'var(--slate-200)' }}
          >
            <strong>Why it didn&rsquo;t finish: </strong>{initFailReason}
          </div>
        ) : null}
        <div className="pps-cta-row">
          <Link to="/care/get-started" className="pps-btn is-primary">
            {initFailed ? 'Try matching again' : 'Start a new request'}
          </Link>
        </div>
        {loadingBundle ? (
          <div className="pps-help" style={{ marginTop: '1rem' }}>Loading the latest from your care team…</div>
        ) : null}

        {/* Wellness escape. /signup creates a Patient Prospect by
            default (care is the home page's primary intent), but
            visitors who actually wanted just the AUMHUM wellness app
            land here too. This single quiet line gives them an
            obvious way out — they keep their ROLE_USER, the wellness
            home / app onboarding takes over, and their stale
            ASSESSMENT-pathway prospect record sits in support
            dashboards filterable by `intakeSubmittedAt is null`. */}
        <p className="pps-wellness-escape">
          Just here for the AUMHUM wellness app?{' '}
          <a href="/" onClick={handleWellnessOptOut}>
            {optingOut ? 'Taking you there…' : 'Continue to the wellness app →'}
          </a>
        </p>
      </Container>
    )
  }

  // Soft-revoke / closed-request state (per D3 in the plan): the user
  // keeps ROLE_PATIENT_PROSPECT but the request was closed by support
  // (PatientProspectInfo.status === 'CLOSED') or the prospect is now a
  // patient (status === 'PROMOTED' — they should usually be on
  // /patient-home/* but we surface a friendly stub here in case they end
  // up on /care/status). Shows softer copy + still offers contact.
  const closedStatuses = new Set(['CLOSED'])
  const promotedStatuses = new Set(['PROMOTED'])
  const prospectStatus = bundle?.prospect?.status
  if (closedStatuses.has(prospectStatus)) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Your care request</Eyebrow>
        <SectionTitle as="h1">
          {firstName === 'there'
            ? 'Thanks for reaching out.'
            : `${firstName}, thanks for reaching out.`}
        </SectionTitle>
        <Lede>
          We weren&rsquo;t able to take this particular request forward, but we&rsquo;re still
          here whenever you need us. You can chat with our care team anytime, or start a
          fresh request whenever you&rsquo;re ready.
        </Lede>

        <Card className="pps-success-card" style={{ borderColor: 'var(--slate-200)' }}>
          <div className="pps-contact-row">
            <a className="pps-contact-btn" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle /> WhatsApp
            </a>
            <a className="pps-contact-btn" href={`tel:${FALLBACK_CONTACT.phone}`}>
              <PhoneCall /> Call us
            </a>
          </div>
        </Card>

        <div className="pps-cta-row" style={{ marginTop: '1rem' }}>
          <Link to="/care/get-started" className="pps-btn is-primary">Start a new request</Link>
        </div>

        <div className="pps-recap" style={{ marginTop: '1.25rem' }}>
          <ShieldCheck style={{ verticalAlign: '-3px', marginRight: 6 }} />
          We keep your information confidential. Reaching out again is always welcome.
        </div>
      </Container>
    )
  }

  if (promotedStatuses.has(prospectStatus)) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Your care request</Eyebrow>
        <SectionTitle as="h1">
          {firstName === 'there'
            ? 'You’re matched with a specialist.'
            : `${firstName}, you’re matched with a specialist.`}
        </SectionTitle>
        <Lede>
          Your request has moved forward and a clinic is now coordinating your care.
          Find your bookings, prescriptions, and updates in your patient home.
        </Lede>
        <div className="pps-cta-row">
          <Link to="/patient-home" className="pps-btn is-primary">Open patient home</Link>
        </div>
        <Card className="pps-success-card" style={{ marginTop: '1rem' }}>
          <div className="pps-contact-row">
            <a className="pps-contact-btn" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle /> WhatsApp
            </a>
            <a className="pps-contact-btn" href={`tel:${FALLBACK_CONTACT.phone}`}>
              <PhoneCall /> Call us
            </a>
          </div>
        </Card>
      </Container>
    )
  }

  // Card ordering rationale (top → bottom):
  //
  //   1. Status header (greeting + brief "submitted" pill + contact
  //      shortcuts). Always visible, gives the user the "your request
  //      is being handled" reassurance in 2 seconds. Compact.
  //
  //   2. Tell us more — CareStatusIntakeForm. This is the user's
  //      single most-impactful next action: the more they share,
  //      the better the care team can match. It used to sit BELOW
  //      the recommendation + assessments cards which buried the
  //      action under information. Promoting it to position 2
  //      makes the path through the form obvious for the
  //      mid-flow user who just landed here from /care/get-started.
  //
  //   3. Initial recommendation (if the triage engine produced one)
  //      — informational, low-action.
  //
  //   4. Self-assessment — with a smart auto-suggested screener
  //      based on the prospect's pathway / specialty / presenting
  //      concerns (e.g. someone who picked Mental health gets a
  //      PHQ-9 recommendation up front). Past takes still surface
  //      as a small list when present.
  //
  //   5. Privacy reassurance.
  const recommendedAssessment = pickRecommendedAssessment(bundle?.prospect)

  return (
    <Container className="pps-shell">
      <Eyebrow>Your care request</Eyebrow>
      <SectionTitle as="h1">
        {firstName === 'there'
          ? 'We’re finding the right specialist for you.'
          : `Hi ${firstName} — we’re finding the right specialist for you.`}
      </SectionTitle>

      {/*
        1) Status strip — kept deliberately compact so it doesn't
        compete with the "Next step" frame below for the user's
        attention. The recap (you picked X) sits inline as a pill;
        contact shortcuts stay as small ghost buttons. Anything more
        verbose pushes the intake form below the fold on phones.
      */}
      <div className="pps-status-strip" role="status">
        <div className="pps-status-strip-line">
          <span className="pps-success-status">
            <CheckCircle2 /> Submitted
            {submittedAt ? (
              <span className="pps-status-strip-when">
                {' · '}{new Date(submittedAt).toLocaleString()}
              </span>
            ) : null}
          </span>
          {recap ? (
            <span className="pps-status-strip-pill">
              {recap.topLabel}
              {recap.subLabel ? <> · {recap.subLabel}</> : null}
            </span>
          ) : null}
        </div>
        <div className="pps-status-strip-contacts">
          <span className="pps-status-strip-note">Care coordinator within 24h.</span>
          <a className="pps-contact-btn pps-contact-btn--sm" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
            <MessageCircle /> WhatsApp
          </a>
          <a className="pps-contact-btn pps-contact-btn--sm" href={`tel:${FALLBACK_CONTACT.phone}`}>
            <PhoneCall /> Call
          </a>
        </div>
      </div>

      {/*
        2) Tell us more — promoted to the top of the action area
        because for a mid-flow user (just signed up at /care/get-started,
        landed here), filling out condition details is the natural
        next action. The whole section is wrapped in a visually
        elevated "Next step" frame (violet accent border, glow, step
        badge) so the form doesn't get lost in the deluge of info
        from the status header above. ?fromAssessment=1 swaps in the
        "your assessment is saved — share more" copy variant.
      */}
      {hasProspectData ? (
        <section
          ref={intakeFormRef}
          className="pps-next-step-frame"
          aria-labelledby="pps-next-step-title"
        >
          <div className="pps-next-step-header">
            <div className="pps-step-badge" aria-hidden="true">
              <span className="pps-step-badge-dot" /> Next step
            </div>
            <h2 id="pps-next-step-title" className="pps-next-step-title">
              {fromAssessment
                ? 'Share more about what you’re going through'
                : 'Tell us more about you'}
            </h2>
            <p className="pps-next-step-lede">
              {fromAssessment
                ? 'Your assessment is saved. To help our care team match you with the right specialist, share a few more details below — what’s been going on, how long, and anything we should know.'
                : 'The more you share, the better our care team can match you to the right specialist. Each section auto-saves as you type — you can come back anytime.'}
            </p>
            <div className="pps-next-step-meta" aria-hidden="true">
              <span className="pps-next-step-arrow">↓</span>
              <span className="pps-next-step-meta-label">Fill the form below — takes 2-3 minutes</span>
            </div>
          </div>
          <CareStatusIntakeForm bundle={bundle} onBundleChange={setBundle} />
        </section>
      ) : null}

      {/* 3) Initial recommendation (informational, low-action). */}
      {reco ? (
        <Card className="pps-card-form" style={{ marginTop: '1rem' }}>
          <Eyebrow>Initial recommendation</Eyebrow>
          <SectionTitle as="h2">{reco.userFacingSummary}</SectionTitle>
          {reco.rationale ? <Lede>{reco.rationale}</Lede> : null}
        </Card>
      ) : null}

      {/* 4) Self-assessment — with auto-suggested screener when the
            user's data points at one (mental-health pathway, anxiety
            chip, etc.). Past takes still surface as a list. */}
      <Card className="pps-card-form" style={{ marginTop: '1rem' }}>
        <Eyebrow>Self-assessment</Eyebrow>
        <SectionTitle as="h2">
          {bundle?.recentAssessments?.length > 0
            ? 'Your assessments'
            : recommendedAssessment
              ? 'Recommended for you'
              : 'Add a private screener to your request'}
        </SectionTitle>
        <Lede>
          {recommendedAssessment && !(bundle?.recentAssessments?.length > 0)
            ? recommendedAssessment.why
            : 'Short clinician-validated screeners can give your care coordinator a clearer first read before they reach out.'}
        </Lede>

        {bundle?.recentAssessments?.length > 0 ? (
          <ul className="pps-assessment-list">
            {bundle.recentAssessments.slice(0, 5).map((a) => {
              const headline =
                Number.isFinite(a?.userScore) && a?.severityBucket
                  ? `${a.severityBucket} · ${a.userScore}`
                  : a?.severityBucket || (Number.isFinite(a?.userScore) ? `${a.userScore}` : null)
              return (
                <li key={a.id || `${a.assessmentSlug}-${a.takenAt}`} className="pps-assessment-item">
                  <span className="pps-assessment-name">
                    {a?.assessmentName || a?.assessmentSlug || 'Assessment'}
                  </span>
                  {headline ? <span className="pps-assessment-headline">{headline}</span> : null}
                  {a?.takenAt ? (
                    <span className="pps-assessment-when">
                      {new Date(a.takenAt).toLocaleDateString()}
                    </span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : null}

        <div className="pps-cta-row">
          {/*
            CTA destination prioritises the auto-suggested screener
            when the user hasn't taken anything yet. Once they have
            past takes the CTA reverts to the generic "take another"
            so the user can browse the full catalog. The link points
            at /wellness/assessment (canonical signed-in URL — public
            /assessment redirects to it via PublicOrWellnessRoute).
          */}
          {recommendedAssessment && !(bundle?.recentAssessments?.length > 0) ? (
            <>
              <Link
                to={`/wellness/assessment/${recommendedAssessment.slug}?fromCareStatus=1`}
                className="pps-btn is-primary"
              >
                Take {recommendedAssessment.name}
                <ClipboardList />
              </Link>
              <Link to="/wellness/assessment" className="pps-btn is-ghost" style={{ marginLeft: '0.5rem' }}>
                Browse all screeners
              </Link>
            </>
          ) : (
            <Link to="/wellness/assessment" className="pps-btn is-primary">
              {bundle?.recentAssessments?.length > 0 ? 'Take another' : 'Start a self-assessment'}
              <ClipboardList />
            </Link>
          )}
        </div>
      </Card>

      {/* 5) Privacy reassurance — sits at the foot of the page. */}
      <div className="pps-recap" style={{ marginTop: '1.25rem' }}>
        <ShieldCheck style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Your details are encrypted and shared only with the assigned care team.
      </div>

      {loadingBundle ? (
        <div className="pps-help" style={{ marginTop: '1rem' }}>Loading the latest from your care team…</div>
      ) : null}

      {PROSPECT_MOCK_MODE ? (
        <div className="pps-help" style={{ marginTop: '1rem' }}>
          (Phase 1 mock session. Backend wiring lands in Phase 2.)
        </div>
      ) : null}
    </Container>
  )
}
